import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';

import {API_URL} from '../constants/api-urls';
import {isEmptyArray} from '../shared/utils';
import {DetailedError, findDetailedErrorMessage} from '../shared/helpers';
import {Response} from '../models/response.model';
import {DistributionGroup} from '../interfaces/distribution-group';
import {AuthService} from './auth.service';


@Injectable()
export class DistributionGroupService {

  public fetchingInProgress = false;


  public selectedDistributionGroup: BehaviorSubject<DistributionGroup> = new BehaviorSubject<DistributionGroup>(null);

  constructor(private http: HttpClient, private toastr: ToastrService, private authService: AuthService) {
    this.loadSelectedDistributionGroup();
  }


  // fetch  distribution group list
  public fetchDistributionGroups(): Observable<DistributionGroup[]> {
    const url = `${API_URL}/account/distribution-groups`;
    this.fetchingInProgress = true;
    return this.http.get<Response>(url)
      .pipe(
        map(response => {
          if (isEmptyArray(response.data)) {
            return [];
          }
          return response.data as DistributionGroup[];
        }),
        catchError(this.handleHttpError('OrderService::fetchDistributionGroups', [])),
        finalize(() => this.fetchingInProgress = false),
      );

  }

  private handleHttpError<T>(operation = 'operation', result?: T) {
    return (error: DetailedError): Observable<T> => {

      const errorMessage = findDetailedErrorMessage(error);
      if (errorMessage) {
        this.toastr.error(errorMessage);
        console.log(operation + ' failed', error);
      }
      return of(result as T);
    };
  }

  public setSelectedDistributionGroup(distributionGroup: DistributionGroup): Observable<boolean> {

    if (!distributionGroup) {
      return of(false);
    }
    const url = `${API_URL}/account/select-distribution-group`;
    return this.http.post(url, {distribution_group_id: distributionGroup.id}).pipe(
      map( () => {
        this.selectedDistributionGroup.next(distributionGroup);
        return true;
      }),
      catchError(this.handleHttpError('OrderService::setSelectedDistributionGroup', false)),
    );
  }

  public resetOrderOnBehalf(): Observable<boolean> {
    const url = `${API_URL}/account/reset-distribution-group`;
    return this.http.post<Response>(url , {}).pipe(
      map( () => {
        this.selectedDistributionGroup.next(null);
        return true;
      }),
      catchError(this.handleHttpError('OrderService::resetOrderOnBehalf', false)),
    );
  }


  private loadSelectedDistributionGroup() {
    if (!this.authService.hasDistributionGroupRole) {
      this.selectedDistributionGroup.next(null);
      return null;
    } else  {
       // in normal  case  it  should get  selected distribution group from user profile
      if (!this.authService.user.selected_distribution_group_id) {
        this.selectedDistributionGroup.next(null);
      }

      this.fetchDistributionGroups().subscribe( groups => {
        if (isEmptyArray(groups)) {
          this.selectedDistributionGroup.next(null);
          return;
        }
        const selectedGroup = groups.find( g => g.id === this.authService.user.selected_distribution_group_id);
        if (selectedGroup) {
          this.selectedDistributionGroup.next(selectedGroup);
        } else {
          this.selectedDistributionGroup.next(null);
        }
      })
    }
  }
}
